import React from 'react'
import hash from '@emotion/hash'
import isEqual from 'lodash/isEqual'

import {
  onButtonTriggerKey,
  onAnchorTriggerKey,
  onAnchorIntercept
} from '../../utils'
import { SubNavigationRelatedLinksProps } from './SubNavigationRelatedLinks'
import { Logo, LogoBeta, LogoQA, LogoPreview } from '../Logo'

interface TopNavigationState {
  activeItem: TopNavigationItem | null
  isExpanded: boolean
  selectedLang: string
  previousState: TopNavigationItem | null
  logoClick: boolean
}

export type TopNavigationItem = {
  __typename?: string
  label?: string
  menus?: SubNavigationRelatedLinksProps[]
  title?: string
  text?: { text: string }
  // New properties for Link type
  linkHref?: { href: string }
  target?: string
}

export type LanguageDetails = {
  languageCode: string
  language: string
}

export type TopNavigationProps = {
  activeMenu: string
  environment: string
  isOverlayOpen: boolean
  items: TopNavigationItem[]
  handleCloseClick: () => void
  handleMenuClick: (item: TopNavigationItem) => void
  handleSearchClick: () => void
  showLangPrefSelector: boolean
  handlePreferencesClick: () => void
  handleClosePopupClick: () => void
  languageSlug?: string
  hasLanguageToggle?: boolean
  toggleLanguage: (selectedLang: string) => void
  languagesToDisplay: LanguageDetails[]
  onAccountSettingsFirstShow: (() => void) | null
  hamburgerCloseClick: () => void
  handleLogoClick: () => void
  handleLogoMenuClick: () => void
}

function getLogo(environment: string) {
  switch (environment) {
    case 'beta':
      return <LogoBeta />
    case 'qa':
    case 'dev':
      return <LogoQA />
    case 'preview':
      return <LogoPreview />
    default:
      return <Logo />
  }
}

export class TopNavigation extends React.Component<
  TopNavigationProps,
  TopNavigationState
> {
  state = {
    activeItem: null,
    isExpanded: false,
    selectedLang: '',
    previousState: null,
    logoClick: false
  }

  handleClick = (item: TopNavigationItem) => {
    const { handleMenuClick, handleSearchClick, isOverlayOpen } = this.props
    this.setState((prevState) => ({
      activeItem: isEqual(prevState.activeItem, item) ? null : item,
      previousState: prevState.activeItem
    }))

    if (isOverlayOpen) {
      handleSearchClick()
    }
    handleMenuClick(item)
  }

  componentDidMount() {
    const { languageSlug } = this.props
    const mappedLocales: any = {
      'zh-hant-tw': 'zh-tw'
    }
    const languageSelected = () => {
      const selectedLang = languageSlug?.toLowerCase() || 'en'
      return mappedLocales[selectedLang] || selectedLang
    }
    this.setState({
      selectedLang: languageSelected()
    })
    window.addEventListener('click', (e) => {
      if (
        e.target.className !== 'frw-overlay__language' &&
        e.target.className !== 'fas fa-globe-americas'
      ) {
        this.setState({ isExpanded: false })
      }
    })
  }

  componentDidUpdate(_prevProps: TopNavigationProps) {
    const { hamburgerCloseClick } = this.props

    /* to close the sub menu overlay in one click whenever we have a activmenu
     * @todo: a better approach for logo click on home page and remove this.state.logoClick dependency
     */
    const { activeMenu } = this.props
    const { logoClick, previousState } = this.state
    if (
      activeMenu?.label === 'hamburger' &&
      previousState &&
      previousState.label !== 'hamburger' &&
      !logoClick
    ) {
      hamburgerCloseClick()
    }
  }

  handleSearchButtonClick = () => {
    const { handleSearchClick } = this.props

    this.setState({ activeItem: null })
    handleSearchClick()
  }

  closePopup = () => {
    const { handleClosePopupClick } = this.props

    this.setState({ activeItem: null })
    handleClosePopupClick()
  }

  handlePreferencesButtonClick = () => {
    const { handlePreferencesClick } = this.props

    this.setState({ activeItem: null })
    handlePreferencesClick()
  }

  logoClickHandler = () => {
    const { handleLogoMenuClick } = this.props
    this.setState({ logoClick: true })
    handleLogoMenuClick()
  }

  openDropdown = () => {
    this.setState({ isExpanded: true })
  }

  setlanguage = (languageCode: string) => {
    const { toggleLanguage } = this.props
    this.setState({ selectedLang: languageCode })
    toggleLanguage(languageCode)
  }

  render() {
    const {
      items,
      isOverlayOpen,
      activeMenu,
      languageSlug = 'en',
      showLangPrefSelector,
      hasLanguageToggle,
      languagesToDisplay
    } = this.props
    const { activeItem, isExpanded, selectedLang } = this.state
    const hamburgerMenuItem: TopNavigationItem = {
      __typename: 'ContentfulNavigation', // eslint-disable-line no-underscore-dangle
      label: 'hamburger',
      menus: [],
      title: 'Hamburger'
    }

    return (
      <>
        <div className="frw-region frw-region--logo">
          <div className="frw-logo__wrapper">
            <a
              aria-label="go to the home page"
              href="/"
              onClick={() => this.logoClickHandler()}
              onKeyDown={onAnchorTriggerKey(() => this.logoClickHandler())}
              data-testid="logo"
            >
              {getLogo('default')}
            </a>
          </div>
        </div>
        <div className="frw-region frw-region--nav">
          <div
            className={
              activeMenu ? 'frw-js-nav-toggle frw-nav--active' : 'frw-js-nav-toggle'
            }
            onClick={() => this.handleClick(hamburgerMenuItem)}
            onKeyDown={onButtonTriggerKey(() =>
              this.handleClick(hamburgerMenuItem)
            )}
            role="button"
            tabIndex={0}
            data-testid="hamburgerMenuItem"
          >
            <span className="frw-nav-line frw-nav-line--1" />
            <span className="frw-nav-line frw-nav-line--2" />
            <span className="frw-nav-line frw-nav-line--3" />
          </div>
          <nav
            className={
              activeMenu
                ? 'frw-navigation__main frw-active'
                : 'frw-navigation__main'
            }
          >
            <ul className="frw-menu frw-menu-level--0">
              {items.map((item) => {
                // Check if this is a Link type vs Navigation type
                const isLinkType = item.__typename === 'ContentfulLink' // eslint-disable-line no-underscore-dangle

                if (isLinkType) {
                  // Render as a direct link
                  return (
                    <li
                      key={hash(item.label)}
                      className="frw-navigation__group"
                    >
                      <a
                        className="frw-overlay frw-overlay__navigation"
                        href={item.linkHref?.href || '#'}
                        target={item.target || '_self'}
                        onClick={this.closePopup}
                        onKeyDown={onAnchorIntercept(this.closePopup)}
                      >
                        {item.label}
                      </a>
                    </li>
                  )
                }
                // Render as a menu trigger (existing behavior)
                return (
                  <li key={hash(item.label)} className="frw-navigation__group">
                    <span
                      className={
                        item === activeItem
                          ? 'frw-overlay frw-overlay__navigation frw-active'
                          : 'frw-overlay frw-overlay__navigation'
                      }
                      onClick={() => this.handleClick(item)}
                      onKeyDown={onButtonTriggerKey(() =>
                        this.handleClick(item)
                      )}
                      role="button"
                      tabIndex={0}
                    >
                      {item.label}
                    </span>
                  </li>
                )
              })}
            </ul>
          </nav>
        </div>
        <div className="frw-region frw-region--user">
          {(hasLanguageToggle || languageSlug === 'ja') && (
            <>
              <div className="frw-region--language">
                <div
                  aria-label="toggle language dropdown"
                  className="frw-overlay__language"
                  onClick={this.openDropdown}
                  onKeyDown={onButtonTriggerKey(this.openDropdown)}
                  role="button"
                  tabIndex={0}
                >
                  <i className="fas fa-globe-americas" />
                </div>
                <div
                  id="language__overlay"
                  role="radiogroup"
                  aria-label="Select language page"
                  className={`${isExpanded && 'frw-active'}`}
                >
                  {languagesToDisplay.map((lang) => (
                    <div
                      key={hash(lang.languageCode)}
                      className="frw-language__row"
                      role="radio"
                      aria-label={lang.language}
                      aria-checked={selectedLang === lang.languageCode}
                      onClick={() => {
                        this.setlanguage(lang.languageCode)
                      }}
                      onKeyDown={onButtonTriggerKey(() => {
                        this.setlanguage(lang.languageCode)
                      })}
                      tabIndex={0}
                    >
                      <span>
                        <input
                          checked={selectedLang === lang.languageCode}
                          type="radio"
                          tabIndex={-1}
                          value={lang.languageCode}
                          aria-hidden="true"
                        />
                        <span aria-hidden="true" className="frw-language_label">
                          {lang.language}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          <div className="frw-region--search">
            {isOverlayOpen ? (
              <span
                aria-label="open search bar"
                className="frw-overlay__search"
                onClick={this.handleSearchButtonClick}
                onKeyDown={onButtonTriggerKey(this.handleSearchButtonClick)}
                role="button"
                tabIndex={0}
                data-testid="handleSearch"
              >
                <i className="fas fa-times" />
              </span>
            ) : (
              <span
                aria-label="open search bar"
                className="frw-overlay__search"
                onClick={this.handleSearchButtonClick}
                onKeyDown={onButtonTriggerKey(this.handleSearchButtonClick)}
                role="button"
                tabIndex={0}
              >
                <i className="fas fa-search" />
              </span>
            )}
          </div>
          {showLangPrefSelector ? (
            <div className="frw-region--search" style={{ marginLeft: '24px' }}>
              <span
                role="button"
                tabIndex={0}
                onClick={this.handlePreferencesButtonClick}
                onKeyDown={onButtonTriggerKey(
                  this.handlePreferencesButtonClick
                )}
                data-testid="handlePreferences"
              >
                {languageSlug.toUpperCase()}
                <i className="fas fa-user-cog" />
              </span>
            </div>
          ) : null}
          <div className="frw-region--login" data-testid="handleContactUs">
            <div className="frw-contact-us">
              <a
                aria-label="contact us"
                href="/contact"
                onClick={this.closePopup}
                onKeyDown={onAnchorIntercept(this.closePopup)}
                className="frw-button frw-contact-link"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </>
    )
  }
}
export default TopNavigation

export const testables = {
  getLogo
}
